import type { ServiceData } from 'serviceWorker/db/service';
import {
  channelServer,
  type ChannelContract,
  type ChannelContractShape,
  type ContractEventMeta,
  type ContractEventOfType,
  type EventPayload,
} from 'shared/channels';
import type { EventType } from 'shared/channels/contract';
import { swSignals } from 'serviceWorker/events/swSignals';
import { DomainMessagesTypes } from 'shared/domain/messages/message';

/**
 * Typed publishers of the service worker: every event goes through
 * `publishEvent`, so its type and payload must match the contract.
 */

/**
 * Publishes an event of the contract to every context and tells the worker's
 * own modules through `swSignals.eventPublished` (the worker never listens to
 * its own channel messages).
 */
export function publishEvent<TShape extends ChannelContractShape, TType extends EventType<TShape>>(
  contract: ChannelContract<TShape>,
  type: TType,
  data: ContractEventOfType<TShape, TType>['data'],
  meta?: ContractEventMeta
): void {
  channelServer.publish(contract, type, data, meta);
  swSignals.eventPublished.emit({
    contractKey: contract.key,
    event: { ...meta, type, data },
  });
}

/** A collection event's payload: the items and whether the download completed. */
export interface CollectionPayload<TItems> {
  hasAll: boolean | undefined;
  items: TItems;
}

/** Reads a collection with its download status. */
export async function readCollectionPayload<TItems>(
  service: { get: () => Promise<ServiceData | undefined> },
  readItems: () => Promise<TItems>
): Promise<CollectionPayload<TItems>> {
  const [status, items] = await Promise.all([service.get(), readItems()]);
  return { hasAll: status?.isDownloaded, items };
}

/** Publishes the payload `load` resolves (e.g. `readCollectionPayload`) as the contract's `type` event. */
export function makeLoadedEventPublisher<
  TShape extends ChannelContractShape,
  TType extends EventType<TShape>,
>(
  contract: ChannelContract<TShape>,
  type: TType,
  load: () => Promise<EventPayload<TShape, TType>>
): () => Promise<void> {
  return async function publishLoadedEvent(): Promise<void> {
    publishEvent(contract, type, await load());
  };
}

/** Publishes one event type of the contract, e.g. an uploaded entity with the `uniqueId` of its command. */
export function makeEventPublisher<
  TShape extends ChannelContractShape,
  TType extends EventType<TShape>,
>(
  contract: ChannelContract<TShape>,
  type: TType
): (data: EventPayload<TShape, TType>, uniqueId?: string) => void {
  return function publishOfType(data, uniqueId): void {
    publishEvent(contract, type, data, { uniqueId });
  };
}

type ClearableShape = ChannelContractShape & {
  events: { [DomainMessagesTypes.clearData]: undefined };
};

/** Tells the contract's listeners to drop what they hold (logout, project change). */
export function makeClearPublisher<TShape extends ClearableShape>(
  contract: ChannelContract<TShape>
): () => void {
  return function publishClear(): void {
    publishEvent(contract, DomainMessagesTypes.clearData, undefined);
  };
}

/** Error meta of an event; `messageId` may come as `null` from the HTTP queue. */
export interface ErrorEventMeta<TData> extends Omit<ContractEventMeta, 'error' | 'messageId'> {
  messageId?: string | null;
  data?: TData;
}

/** Publishes a failure of any event type of the contract, with the error and its correlation fields. */
export function makeErrorPublisher<TShape extends ChannelContractShape>(contract: ChannelContract<TShape>) {
  return function publishError<TType extends EventType<TShape>>(
    type: TType,
    error: unknown,
    { messageId, data, uniqueId }: ErrorEventMeta<EventPayload<TShape, TType>> = {}
  ): void {
    publishEvent(contract, type, data, {
      error,
      uniqueId,
      messageId: messageId ?? undefined,
    });
  };
}
